import type { Client } from '../../../core/models/Client';
import type { TimeEntry } from '../../../core/models/TimeEntry';
import { TimeEntryStatus } from '../../../core/models/TimeEntry';
import { UserRole } from '../../../core/models/User';

export type DuplicateResolution = 'ignore' | 'merge';

export interface ParsedImportRow {
  raw: Record<string, unknown>;
  index: number;
}

/** Stable key for matching duplicates (same user, client, minute bucket, duration, project). */
export function timeEntryFingerprint(entry: {
  organizationId?: string;
  clientId?: string;
  userId: string;
  startTime: Date;
  duration: number;
  projectId?: string;
}): string {
  const tenant = entry.clientId || entry.organizationId || '';
  const pid = entry.projectId ?? '';
  const startMin = Math.floor(entry.startTime.getTime() / 60_000);
  return `${tenant}|${entry.userId}|${startMin}|${entry.duration}|${pid}`;
}

function parseUnknownDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === 'number' && !isNaN(value)) return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && value !== null && 'seconds' in value) {
    const s = (value as { seconds: number }).seconds;
    return new Date(s * 1000);
  }
  return null;
}

const STATUS_SET = new Set<string>(Object.values(TimeEntryStatus));

function normalizeStatus(v: unknown): TimeEntryStatus {
  if (typeof v === 'string' && STATUS_SET.has(v)) {
    return v as TimeEntryStatus;
  }
  return TimeEntryStatus.PENDING;
}

export type NormalizedImport = Omit<TimeEntry, 'id' | 'createdAt' | 'updatedAt'>;

export interface NormalizeResult {
  ok: boolean;
  errors: string[];
  value?: NormalizedImport;
}

/**
 * Time entries still store `organizationId` in Firestore (parent client / tenant scope).
 * Resolve from: per-row overrides, auth defaultScopeId (clientId or legacy organizationId),
 * or by looking up a row's project `clientId` / `projectId` in `clients`.
 */
function resolveScopeOrganizationId(
  raw: Record<string, unknown>,
  ctx: {
    defaultScopeId: string;
    allowPerRowScopeOverride: boolean;
    clients: Client[];
  }
): { organizationId: string; errors: string[] } {
  if (ctx.allowPerRowScopeOverride && typeof raw.organizationId === 'string' && raw.organizationId.trim()) {
    return { organizationId: raw.organizationId.trim(), errors: [] };
  }
  if (typeof raw.parentClientId === 'string' && raw.parentClientId.trim()) {
    return { organizationId: raw.parentClientId.trim(), errors: [] };
  }

  const projectDocId =
    typeof raw.clientId === 'string' && raw.clientId.trim()
      ? raw.clientId.trim()
      : typeof raw.projectId === 'string' && raw.projectId.trim()
        ? raw.projectId.trim()
        : undefined;

  if (projectDocId) {
    const c = ctx.clients.find((x) => x.id === projectDocId);
    if (c) {
      if (c.entityType === 'client') {
        return { organizationId: c.id, errors: [] };
      }
      const parent = (c.clientId || c.organizationId || '').trim();
      if (parent) {
        return { organizationId: parent, errors: [] };
      }
      return {
        organizationId: '',
        errors: [
          'This project has no parent client on file — add parentClientId on the row, or fix the project record.',
        ],
      };
    }
    return {
      organizationId: '',
      errors: [
        'Unknown clientId/projectId (not in the loaded project list). Add parentClientId for the parent client, or fix the id.',
      ],
    };
  }

  if (ctx.defaultScopeId) {
    return { organizationId: ctx.defaultScopeId, errors: [] };
  }

  return {
    organizationId: '',
    errors: [
      'Missing client scope: set your profile client, add parentClientId on the row, or use a clientId/projectId from this app’s project list.',
    ],
  };
}

export function normalizeImportRecord(
  raw: Record<string, unknown>,
  ctx: {
    defaultScopeId: string;
    defaultUserId: string;
    allowPerRowScopeOverride: boolean;
    clients: Client[];
    /** When set, CLIENT_ADMIN rows must use a uid in this set. Ignored for SUPER_ADMIN. */
    allowedUserIds?: Set<string> | null;
    currentUserRole?: UserRole;
    currentUid?: string;
  }
): NormalizeResult {
  const errors: string[] = [];

  const description =
    typeof raw.description === 'string'
      ? raw.description.trim()
      : raw.description != null
        ? String(raw.description).trim()
        : '';
  if (!description) errors.push('description is required');

  const scope = resolveScopeOrganizationId(raw, {
    defaultScopeId: ctx.defaultScopeId,
    allowPerRowScopeOverride: ctx.allowPerRowScopeOverride,
    clients: ctx.clients,
  });
  const organizationId = scope.organizationId;
  errors.push(...scope.errors);

  let userId = ctx.defaultUserId;
  if (typeof raw.userId === 'string' && raw.userId.trim()) {
    userId = raw.userId.trim();
  }
  if (!userId) errors.push('userId is required');

  const startTime = parseUnknownDate(raw.startTime);
  if (!startTime) errors.push('startTime is invalid or missing');

  let endTime = parseUnknownDate(raw.endTime);
  let duration: number | undefined =
    typeof raw.duration === 'number' && !isNaN(raw.duration) ? Math.round(raw.duration) : undefined;

  if (startTime && endTime) {
    if (endTime <= startTime) errors.push('endTime must be after startTime');
    else if (duration === undefined) {
      duration = Math.round((endTime.getTime() - startTime.getTime()) / 60_000);
    }
  } else if (startTime && duration !== undefined && duration >= 0) {
    endTime = new Date(startTime.getTime() + duration * 60_000);
  } else {
    if (!endTime) errors.push('Provide endTime or duration (minutes)');
    if (duration === undefined && !endTime) errors.push('duration (minutes) is required if endTime is missing');
  }

  if (duration !== undefined && duration < 0) errors.push('duration must be non-negative');

  const clientIdRaw = raw.clientId ?? raw.projectId;
  let projectId: string | undefined;
  if (typeof clientIdRaw === 'string' && clientIdRaw.trim()) {
    projectId = clientIdRaw.trim();
  } else if (clientIdRaw === null || clientIdRaw === '') {
    projectId = undefined;
  }

  let type: string | undefined;
  if (typeof raw.type === 'string' && raw.type.trim()) {
    type = raw.type.trim();
  }

  const billable = typeof raw.billable === 'boolean' ? raw.billable : true;

  const status = normalizeStatus(raw.status);

  let contractId: string | undefined;
  if (typeof raw.contractId === 'string' && raw.contractId.trim()) contractId = raw.contractId.trim();

  let serviceId: string | undefined;
  if (typeof raw.serviceId === 'string' && raw.serviceId.trim()) serviceId = raw.serviceId.trim();

  let taskId: string | undefined;
  if (typeof raw.taskId === 'string' && raw.taskId.trim()) taskId = raw.taskId.trim();

  let taskTitle: string | undefined;
  if (typeof raw.taskTitle === 'string' && raw.taskTitle.trim()) taskTitle = raw.taskTitle.trim();

  if (errors.length > 0 || !startTime || !endTime || duration === undefined || !organizationId || !userId) {
    return { ok: false, errors };
  }

  if (ctx.currentUserRole === UserRole.PROJECT_ADMIN && ctx.currentUid && userId !== ctx.currentUid) {
    errors.push('userId must be your own Firebase Auth uid (project admins cannot attribute time to others).');
    return { ok: false, errors };
  }

  if (
    ctx.currentUserRole === UserRole.CLIENT_ADMIN &&
    ctx.allowedUserIds &&
    ctx.allowedUserIds.size > 0 &&
    !ctx.allowedUserIds.has(userId)
  ) {
    errors.push('userId must belong to a user in your organization.');
    return { ok: false, errors };
  }

  const value: NormalizedImport = {
    clientId: organizationId,
    organizationId,
    userId,
    description,
    startTime,
    endTime,
    duration,
    billable,
    status,
  };
  if (projectId) value.projectId = projectId;
  if (type) value.type = type;
  if (contractId) value.contractId = contractId;
  if (serviceId) value.serviceId = serviceId;
  if (taskId) value.taskId = taskId;
  if (taskTitle) value.taskTitle = taskTitle;

  return { ok: true, errors: [], value };
}

export function parseImportJson(text: string): { ok: true; rows: Record<string, unknown>[] } | { ok: false; error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Invalid JSON — check commas, quotes, and brackets.' };
  }

  if (Array.isArray(parsed)) {
    return { ok: true, rows: parsed.filter((x) => x !== null && typeof x === 'object') as Record<string, unknown>[] };
  }
  if (parsed && typeof parsed === 'object' && 'entries' in parsed && Array.isArray((parsed as { entries: unknown }).entries)) {
    const entries = (parsed as { entries: unknown[] }).entries;
    return {
      ok: true,
      rows: entries.filter((x) => x !== null && typeof x === 'object') as Record<string, unknown>[],
    };
  }
  return { ok: false, error: 'JSON must be an array of records or { "entries": [ ... ] }.' };
}

export interface FieldDiff {
  field: string;
  before: string;
  after: string;
}

const DIFF_FIELDS: (keyof NormalizedImport)[] = [
  'description',
  'startTime',
  'endTime',
  'duration',
  'billable',
  'status',
  'clientId',
  'projectId',
  'type',
  'contractId',
  'serviceId',
  'taskId',
  'taskTitle',
  'organizationId',
  'userId',
];

function fmt(v: unknown): string {
  if (v instanceof Date) return v.toISOString();
  if (v === undefined) return '—';
  return String(v);
}

export function diffTimeEntries(existing: TimeEntry, incoming: NormalizedImport): FieldDiff[] {
  const out: FieldDiff[] = [];
  for (const key of DIFF_FIELDS) {
    const a = existing[key];
    const b = incoming[key];
    if (key === 'startTime' || key === 'endTime') {
      const ad = a instanceof Date ? a.getTime() : NaN;
      const bd = b instanceof Date ? b.getTime() : NaN;
      if (ad !== bd) {
        out.push({ field: key, before: fmt(a), after: fmt(b) });
      }
      continue;
    }
    if (a !== b && (a ?? undefined) !== (b ?? undefined)) {
      out.push({ field: key, before: fmt(a), after: fmt(b) });
    }
  }
  return out;
}

export function findDuplicateMatch(
  normalized: NormalizedImport,
  existing: TimeEntry[],
  explicitId?: string
): TimeEntry | undefined {
  if (explicitId) {
    const byId = existing.find((e) => e.id === explicitId);
    if (byId) return byId;
  }
  const fp = timeEntryFingerprint(normalized);
  return existing.find(
    (e) =>
      (e.clientId || e.organizationId) === (normalized.clientId || normalized.organizationId) &&
      e.userId === normalized.userId &&
      timeEntryFingerprint(e) === fp
  );
}
